// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getTheme } from "./theme";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  isAutoTheme: boolean;
  resetToAuto: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: "light",
  toggleTheme: () => {},
  isAutoTheme: true,
  resetToAuto: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

function getSystemMode(): ThemeMode {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem("staroneThemeMode");
    if (stored === "light" || stored === "dark") return stored;
    return "dark"; // Default to dark (VS Code style)
  });
  const [isAuto, setIsAuto] = useState(() => {
    return !localStorage.getItem("staroneThemeMode");
  });

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (!isAuto) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setMode(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [isAuto]);

  const toggleTheme = () => {
    const next: ThemeMode = mode === "light" ? "dark" : "light";
    setMode(next);
    setIsAuto(false);
    localStorage.setItem("staroneThemeMode", next);
  };

  const resetToAuto = () => {
    localStorage.removeItem("staroneThemeMode");
    setIsAuto(true);
    setMode(getSystemMode());
  };

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, isAutoTheme: isAuto, resetToAuto }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ fullDate: "DD/MMM/YYYY", keyboardDate: "DD/MMM/YYYY" }}>
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}
